import { useState, useEffect, useCallback } from 'react';
import { Store } from '../types/store';
import { getAllStores, deleteStore, updateExistingStore } from '../services/storeService';
import { useStore } from '../contexts/StoreContext';
import { useAlert } from './useAlert';
import { useAdminGuard } from './useAdminGuard';
import { extractApiError } from '../utils/errorUtils';
import { PaginationParams } from '../types/pagination';

const DEFAULT_LIMIT = 20;

export function useAllStores() {
  const { isAdmin } = useAdminGuard();
  const { fetchUserStores } = useStore();
  const { alert, showAlert, hideAlert } = useAlert();
  const [stores, setStores] = useState<Store[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [isLoading, setIsLoading] = useState(true);

  const fetchStores = useCallback(async () => {
    if (!isAdmin) return;
    setIsLoading(true);
    try {
      const params: PaginationParams = { page, limit, search: search || undefined, sortBy, sortOrder };
      const result = await getAllStores(params);
      setStores(result.items);
      setTotal(result.total);
    } catch (err) {
      setStores([]);
      setTotal(0);
      showAlert(extractApiError(err, 'Failed to load stores.'), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin, page, limit, search, sortBy, sortOrder, showAlert]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  const handleDelete = useCallback(async (storeId: number) => {
    if (!window.confirm('Are you sure you want to delete this store? This action cannot be undone and will remove all associated data.')) {
      return;
    }
    hideAlert();
    try {
      await deleteStore(storeId);
      setStores((prev) => prev.filter((s) => s.id !== storeId));
      setTotal((t) => Math.max(0, t - 1));
      fetchUserStores();
      showAlert('Store deleted successfully.', 'success');
    } catch (err) {
      showAlert(extractApiError(err, 'Failed to delete store.'), 'error');
    }
  }, [hideAlert, showAlert, fetchUserStores]);

  const handleToggleActive = useCallback(async (store: Store) => {
    hideAlert();
    try {
      const updated = await updateExistingStore(store.id, { is_active: !store.is_active });
      setStores((prev) => prev.map((s) => (s.id === store.id ? updated.store : s)));
      fetchUserStores();
      showAlert(updated.store.is_active ? 'Store enabled successfully.' : 'Store disabled successfully.', 'success');
    } catch (err) {
      showAlert(extractApiError(err, 'Failed to update store status.'), 'error');
    }
  }, [hideAlert, showAlert, fetchUserStores]);

  const totalPages = Math.ceil(total / limit) || 1;

  return {
    isAdmin,
    stores,
    isLoading,
    alert,
    hideAlert,
    fetchStores,
    handleDelete,
    handleToggleActive,
    pagination: {
      page,
      limit,
      total,
      totalPages,
      setPage,
      setLimit,
      setSearch,
      setSortBy,
      setSortOrder,
      search,
      sortBy,
      sortOrder,
    },
  };
}
